/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsdoc/require-param */
/* eslint-disable jsdoc/check-param-names */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsdoc/require-jsdoc */

import React, { CSSProperties, ReactElement, useCallback, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  ColumnDef,
  Header,
  Table,
  Cell
} from '@tanstack/react-table';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  DragEndEvent,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TruncatedText from './truncatedText';
import { Filters } from '../services/localstorage.service';
import useStorageState from '../hooks/useStorageState';

type TanStackTableTableProps<T> = {
  columns: ColumnDef<T, any>[];
  data: T[];
  tdClassName?: string;
  thClassName?: string;
  columOrderStorageKey: Filters;
};

/**
 * A functional component that represents a draggable cell in the table.
 * @param cell - The cell data to render.
 * @param tdClassName - Additional CSS classes for the cell.
 * @param index - The index of the cell.
 * @returns A ReactElement representing the table cell.
 */
function DragAlongCell<T>({
  cell,
  tdClassName,
  index
}: {
  cell: Cell<T, unknown>;
  tdClassName?: string;
  index: number;
}): ReactElement {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: 'relative',
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s ease-in-out',
    width: cell.column.getSize()
  };

  const value = flexRender(cell.column.columnDef.cell, cell.getContext());

  return (
    <td
      ref={setNodeRef}
      style={{
        ...style,
        minWidth: `${cell.column.getSize()}px`,
        maxWidth: `${cell.column.getSize()}px`
      }}
      className={`${tdClassName || 'px-6 py-3 bg-container-500'} ${
        index === 0 ? 'text-left left-0 z-[1]' : 'text-center'
      }`}
    >
      {value}
    </td>
  );
}

/**
 * A functional component that represents a draggable table header.
 * @param header - The header data to render.
 * @param thClassName - Additional CSS classes for the header.
 * @param index - The index of the header.
 * @param table - The table instance from react-table.
 * @returns A ReactElement representing the table header.
 */
function DraggableTableHeader<T>({
  header,
  thClassName,
  index,
  table,
  handleResizerClick
}: {
  header: Header<T, unknown>;
  thClassName?: string;
  index: number;
  table: Table<T>;
  handleResizerClick: () => void;
}): ReactElement {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.id
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: 'relative',
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s ease-in-out',
    whiteSpace: 'nowrap',
    width: header.getSize(),
    zIndex: isDragging ? 1 : 0
  };

  let clickTimer: any = null;
  let isDraggingLocal = false;

  const handleMouseDown = (event: React.MouseEvent): void => {
    if (header.column.getIsResizing()) return;

    isDraggingLocal = false;
    clickTimer = setTimeout(() => {
      isDraggingLocal = true;
      listeners?.onMouseDown?.(event);
    }, 150); // Delay before drag starts (150ms)
  };

  const handleMouseUp = (event: React.MouseEvent): void => {
    if (clickTimer) clearTimeout(clickTimer);

    if (!isDragging) {
      const sortHandler = header.column.getToggleSortingHandler?.();
      if (sortHandler) {
        sortHandler(event);
      }
    }
  };

  const handleMouseMove = (): void => {
    if (clickTimer) {
      clearTimeout(clickTimer);
      isDraggingLocal = true;
    }
  };

  return (
    <th
      ref={setNodeRef}
      colSpan={header.colSpan}
      style={{
        ...style,
        minWidth: `${header.getSize()}px`,
        maxWidth: `${header.getSize()}px`,
        zIndex: header.column.getIsResizing() ? 100 : 0
      }}
      className={`${thClassName || 'justify-center px-6 py-3 bg-container-500'} ${
        index === 0 ? 'items-start text-start sticky left-0 top-0 z-[3]' : 'items-center'
      } overflow-visible`}
    >
      <button
        onMouseDown={header.getResizeHandler()}
        type="button"
        onTouchStart={header.getResizeHandler()}
        onClick={handleResizerClick}
        className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
        style={{
          transform: header.column.getIsResizing()
            ? `translateX(${table.getState().columnSizingInfo.deltaOffset}px)`
            : ''
        }}
      />
      <div className={`flex items-center ${index !== 0 && 'justify-center'}`}>
        {!header.isPlaceholder && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            className="inline-flex"
          >
            <TruncatedText
              text={flexRender(header.column.columnDef.header, header.getContext()) as string}
            />
            <span className="material-icons-outlined text-xs">
              {{
                asc: 'arrow_downward',
                desc: 'arrow_upward'
              }[header.column.getIsSorted() as string] ?? null}
            </span>
          </button>
        )}
        {/* <button
          type="button"
          className="mx-1 material-icons-outlined text-lg cursor-grab inline-flex "
        >
          drag_handle
        </button> */}
      </div>
    </th>
  );
}

/**
 * A Devices Table Component with drag-and-drop functionality.
 * @param props - Devices Table Component Props.
 * @returns A React Component.
 */
export default function TanstackTable<T extends any>({
  columns,
  data,
  tdClassName,
  thClassName,
  columOrderStorageKey
}: TanStackTableTableProps<T>): ReactElement {
  const defaultColumnOrder = columns.map((c) => c.id!);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnOrder, setColumnOrder] = useStorageState<string[]>(
    columOrderStorageKey,
    defaultColumnOrder
  );
  const [columnSizing, setColumnSizing] = useState({});
  const table = useReactTable({
    columns,
    data,
    defaultColumn: {
      size: 50
    },
    columnResizeMode: 'onEnd',
    state: {
      sorting,
      columnOrder,
      columnSizing
    },
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder
  });

  const handleResizerClick = (columnId: any): void => {
    const element = columns.find((item) => item.id === columnId);
    setColumnSizing((old) => ({
      ...old,
      [columnId]: element?.size || 50 // Reset to default width
    }));
  };

  /**
   * Handles the drag end event by updating the column order.
   * @param event - The drag end event.
   */
  function handleDragEnd(event: DragEndEvent): any {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setColumnOrder((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over?.id as string);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      // eslint-disable-next-line react/jsx-no-bind
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <table
        style={{
          minWidth: '100%',
          width: table.getCenterTotalSize()
        }}
        className="overflow-visible relative"
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              className="text-center text-gray-400 text-xs font-medium sticky top-0 z-[2] bg-container-500"
              key={headerGroup.id}
            >
              <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                {headerGroup.headers.map((header, index) => (
                  <DraggableTableHeader
                    key={header.id}
                    index={index}
                    thClassName={thClassName}
                    header={header}
                    handleResizerClick={() => handleResizerClick(header.id)}
                    table={table}
                  />
                ))}
              </SortableContext>
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr className="bg-white dark:bg-container-500 font-medium text-gray-400" key={row.id}>
              {row.getVisibleCells().map((cell, index) => (
                <SortableContext
                  key={cell.id}
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  <DragAlongCell<T>
                    key={cell.id}
                    index={index}
                    tdClassName={tdClassName}
                    cell={cell}
                  />
                </SortableContext>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </DndContext>
  );
}
